import React, { useState, useRef, useEffect } from "react";
import { Microphone, GoAway, Upload } from "../Icons/Interface";

enum BitRateQuality {
	LowQuality = 16000, // 16 kbps
	StandardQuality = 48000, // 48 kbps
	HighQuality = 128000, // 128 kbps
	ArchivalQuality = 256000 // 256 kbps
}

export enum AudioMimeTypes {
	Webm = "audio/webm",
	Webm_opus = "audio/webm;codecs=opus",
	Ogg_opus = "audio/ogg;codecs=opus",
	Mp4a = "audio/mp4;codecs=mp4a",
	Mp4 = "audio/mp4",
	Wav = "audio/wav",
	Ogg = "audio/ogg"
}
interface AudioRecorderProps {
	// Color for the pulsing microphone when active
	recordingColor?: string;
	// Upload endpoint URL or function that returns a Promise
	onRecordingComplete: (audioBlob: Blob) => Promise<void>;
	// Optional callback when recording starts
	onRecordingStart?: () => void;
	// Optional callback when recording is cancelled
	onRecordingCancel?: () => void;
	// Size of the microphone icon (in pixels)
	size?: number;
	// Maximum recording duration in seconds (0 for unlimited)
	maxDuration?: number;
	// Audio format: 'audio/webm', 'audio/mp3', 'audio/wav' etc.
	audioFormat?: AudioMimeTypes;
	// Additional CSS classes
	className?: string;
}

const AudioInput: React.FC<AudioRecorderProps> = ({
	recordingColor = "#6366F1", // Default indigo color
	onRecordingComplete,
	onRecordingStart,
	onRecordingCancel,
	size = 64,
	maxDuration = 0,
	audioFormat = AudioMimeTypes.Webm,
	className = ""
}) => {
	// State
	const [isRecording, setIsRecording] = useState<boolean>(false);
	const [isPulsing, setIsPulsing] = useState<boolean>(false);
	const [recordingTime, setRecordingTime] = useState<number>(0);
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [permissionDenied, setPermissionDenied] = useState<boolean>(false);

	// Refs
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const audioChunksRef = useRef<BlobPart[]>([]);
	const animationFrameRef = useRef<number | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const timerRef = useRef<NodeJS.Timeout | null>(null);

	// Clean up function
	const cleanup = () => {
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}

		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}

		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}

		if (mediaRecorderRef.current) {
			if (mediaRecorderRef.current.state !== "inactive") {
				mediaRecorderRef.current.stop();
			}
			mediaRecorderRef.current = null;
		}

		audioChunksRef.current = [];
		//setAudioLevel(Array(20).fill(0));
	};

	// Stop recording
	const stopRecording = () => {
		if (
			mediaRecorderRef.current &&
			mediaRecorderRef.current.state !== "inactive"
		) {
			mediaRecorderRef.current.stop();
		}
	};

	// Start recording
	const startRecording = async () => {
		cleanup();

		try {
			// Documentation: https://developer.mozilla.org/en-US/docs/Web/API/Web_Audio_API
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			streamRef.current = stream;

			// Set up audio analyzer for waveform
			const audioContext = new AudioContext();
			const source = audioContext.createMediaStreamSource(stream);

			//TODO: Can we install an AudioAnalyser that looks for a configurable ammount of white noise - that will trigger the Toggle recording feature.
			//TODO: Can we install an AudioAnalyser that Can draw ... a primitive static wav form - that can be rendered elsewhere on the page.
			const analyser = audioContext.createAnalyser();
			analyser.fftSize = 64;
			source.connect(analyser);
			analyserRef.current = analyser;

			// Create media recorder
			const mediaRecorder = new MediaRecorder(stream, {
				mimeType: audioFormat,
				audioBitsPerSecond: BitRateQuality.ArchivalQuality
			});
			mediaRecorderRef.current = mediaRecorder;

			// Event handlers
			mediaRecorder.ondataavailable = (event) => {
				if (event.data.size > 0) {
					audioChunksRef.current.push(event.data);
				}
			};

			mediaRecorder.onstop = () => {
				if (audioChunksRef.current.length > 0) {
					const audioBlob = new Blob(audioChunksRef.current, {
						type: audioFormat
					});
					//setAudioData(audioBlob);

					// Reset UI
					setIsRecording(false);
					setIsPulsing(false);
					console.log(`Recorder Stopped Recording[${isRecording}]`);

					// Upload the recorded audio
					handleUpload(audioBlob);
				}

				cleanup();
			};

			// Start recording
			mediaRecorder.start();
			setIsRecording(true);
			setIsPulsing(true);
			console.log(`Recorder Started Recording[${isRecording}]`);

			if (onRecordingStart) {
				onRecordingStart();
			}

			// Update recording time
			timerRef.current = setInterval(() => {
				setRecordingTime((prev) => {
					// Check if max duration is reached
					if (maxDuration > 0 && prev >= maxDuration) {
						stopRecording();
						return prev;
					}
					return prev + 1;
				});
			}, 1000);

			// Update waveform
			const updateWaveform = () => {
				if (analyserRef.current) {
					const dataArray = new Uint8Array(
						analyserRef.current.frequencyBinCount
					);
					analyserRef.current.getByteFrequencyData(dataArray);

					// Convert frequency data to waveform heights
					// //const newLevels = Array(20)
					// //	.fill(0)
					// //	.map((_, i) => {
					// //		const index = Math.floor((i * dataArray.length) / 20);
					// //		return dataArray[index] / 255; // Normalize to 0-1
					// //	});

					//setAudioLevel(newLevels);
				}

				if (isRecording) {
					animationFrameRef.current = requestAnimationFrame(updateWaveform);
				}
			};

			updateWaveform();
		} catch (error) {
			console.error("Error accessing microphone:", error);
			setPermissionDenied(true);

			if (onRecordingCancel) {
				onRecordingCancel();
			}
		}
	};

	// Handle upload
	const handleUpload = async (blob: Blob) => {
		try {
			setIsUploading(true);
			await onRecordingComplete(blob);
			setIsUploading(false);
			//setAudioData(null);
		} catch (error) {
			console.error("Error uploading audio:", error);
			setIsUploading(false);
		}
	};

	// Toggle recording
	const toggleRecording = () => {
		if (isRecording) {
			stopRecording();
		} else {
			setRecordingTime(0);
			startRecording();
		}
	};

	// Cancel recording
	// //const cancelRecording = () => {
	// //	if (isRecording) {
	// //		cleanup();
	// //		setIsRecording(false);
	// //		setIsPulsing(false);
	// //		console.log(`Recorder Cancelled Recording[${isRecording}]`);
	// //		audioChunksRef.current = [];
	// //
	// //		if (onRecordingCancel) {
	// //			onRecordingCancel();
	// //		}
	// //	}
	// //};

	// Define the keydown handler
	// //const handleKeyDown = (event: KeyboardEvent) => {
	// //	console.log(
	// //		`Microphone KeyDown Called Recording:[${isRecording}] key:[${event.key}] Target:[${event.target}]`
	// //	);
	// //
	// //	if (event.key === "Escape") {
	// //		event.preventDefault();
	// //		cancelRecording();
	// //		event.stopPropagation();
	// //	}
	// //};

	// install KeyDown handler
	// useEffect(() => {
	// 	const handleKeyDown = (event: KeyboardEvent) => {
	// 		if (event.key === "Escape") {
	// 			cancelRecording();
	// 			event.stopPropagation();
	// 		}
	// 	};
	// 	window.addEventListener("keydown", handleKeyDown);
	// 	return () => {
	// 		window.removeEventListener("keydown", handleKeyDown);
	// 	};
	// }, []);

	// Clean up on unmount
	useEffect(() => {
		return () => {
			cleanup();
		};
		// eslint-disable-next-line
	}, []); // Empty dependency array ensures this runs only once on mount

	// Format time for display
	const formatTime = (seconds: number): string => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	const microphoneColor = isRecording
		? `text-[${recordingColor}]`
		: permissionDenied
			? "text-primary-inactive"
			: "text-primary-active";

	return (
		<div className={`flex flex-col items-center ${className}`}>
			{/* Recording Time */}
			<div
				className={`mt-2 text-sm font-medium text-primary-inactive ${isRecording ? "opactiy-100" : "opactiy-0"}`}
			>
				{formatTime(recordingTime)}
			</div>

			{/* Main Microphone Button */}
			<button
				onClick={toggleRecording}
				disabled={isUploading || permissionDenied}
				className={`relative flex items-center justify-center rounded-full                    
                   transition-all duration-300 ease-in-out                   
                   hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-opacity-50
                   ${isUploading ? "cursor-not-allowed opacity-70" : "cursor-pointer"} 
                   ${permissionDenied ? "bg-red-50 cursor-not-allowed" : ""}
                   ${
											isPulsing
												? `ring-opacity-50 ring-[${microphoneColor}] ring-4 animate-pulse`
												: "none"
										}`}
			>
				{/* Loading State */}
				{isUploading && (
					<div
						className={`${microphoneColor} ${isPulsing ? "animate-pulse" : ""} bg-primary-backgound`}
					>
						{Upload()}
					</div>
				)}

				{/* Stop Recording Icon */}
				{!isUploading && isRecording && (
					<div
						className={`${microphoneColor} ${isPulsing ? "animate-pulse" : ""} bg-primary-backgound`}
					>
						{GoAway()}
					</div>
				)}

				{/* Microphone Icon */}
				{!isUploading && !isRecording && (
					<div
						className={`${microphoneColor} ${isPulsing ? "animate-pulse" : ""} bg-primary-backgound`}
					>
						{Microphone()}
					</div>
				)}
			</button>

			{/* Permission Denied Message */}
			{permissionDenied && (
				<div className="mt-3 text-sm text-red-600">
					Microphone access denied. Please check your browser permissions.
				</div>
			)}
		</div>
	);
};

export default AudioInput;
